/**
 * ToolRegistry — Dynamic registry for Meneer AI tools.
 *
 * Modules call ToolRegistry.register() to add tools Meneer can invoke via
 * Groq function calling. The schemas are passed directly to the `tools`
 * parameter of the Groq API — no system-prompt XML tags needed.
 *
 * Design constraints (from roadmap):
 *  - Max ~15 tools at launch to keep selection accuracy high
 *  - Every tool description answers: what / when / params / example
 *  - Destructive tools (delete_*) require explicit user confirmation
 *  - Failures are always surfaced — never silent
 *
 * ES module. Loaded via <script type="module">, so it is deferred and now evaluates
 * after every classic script rather than in its listed position. Safe because its
 * only consumer, meneer-chat.js, reads `window.ToolRegistry?.` from inside async
 * functions, never at parse time.
 *
 * Handlers below reference bare globals (Storage, showNotification, applyTheme).
 * Those still resolve from module scope through the global object, and only at
 * call time — long after every classic script has run.
 *
 * Exposes: window.ToolRegistry (transition shim, AGENTS.md SKILL 1)
 */

const ToolRegistry = (function () {

    const _tools = {};  // { name: { schema, handler } }

    // ── Core API ───────────────────────────────────────────────────────────────

    /**
     * Register a tool Meneer can call.
     * @param {{ name, description, parameters, handler }} tool
     */
    function register({ name, description, parameters, handler }) {
        _tools[name] = {
            schema: {
                type: 'function',
                function: { name, description, parameters }
            },
            handler
        };
    }

    /** Returns the tools array for the Groq `tools` parameter */
    function getSchemas() {
        return Object.values(_tools).map(t => t.schema);
    }

    /**
     * Execute a tool by name. Returns a result string for the tool message.
     * Never throws — errors are returned as strings so the chat loop continues.
     */
    async function execute(name, args) {
        const tool = _tools[name];
        if (!tool) {
            console.warn(`[ToolRegistry] Unknown tool: "${name}"`);
            return `Error: unknown tool "${name}"`;
        }
        try {
            const result = await tool.handler(args);
            return result != null ? String(result) : 'Done.';
        } catch (err) {
            console.error(`[ToolRegistry] "${name}" threw:`, err);
            return `Error executing ${name}: ${err.message}`;
        }
    }

    function has(name)  { return !!_tools[name]; }
    function list()     { return Object.keys(_tools); }

    // ── Tool Registrations ─────────────────────────────────────────────────────
    // Registered immediately so they're available before DOMContentLoaded.
    // Handler functions reference globals (Storage, renderTasks, showNotification,
    // etc.) which are guaranteed to exist by the time any handler is invoked.

    // ── TASKS ──────────────────────────────────────────────────────────────────

    register({
        name: 'list_tasks',
        description:
            'List the student\'s tasks with their due dates and priorities. ' +
            'ALWAYS call this before answering ANY question about what the student has to do — ' +
            'how many tasks, which are due on a date, what is overdue, which to start. ' +
            'Never guess a count or invent task titles: only this tool knows them. ' +
            'Optional filter: "pending" (default), "completed", or "all". ' +
            'Example prompts: "how many tasks do I have?", "what\'s due on Friday?", ' +
            '"which task should I start with?"',
        parameters: {
            type: 'object',
            properties: {
                filter: {
                    type: 'string',
                    enum: ['pending', 'completed', 'all'],
                    description: 'Which tasks to return — default "pending"'
                }
            }
        },
        handler({ filter = 'pending' } = {}) {
            const tasks     = window.Storage.loadTasks();
            const pending   = tasks.pending   || [];
            const completed = tasks.completed || [];

            const chosen = filter === 'completed' ? completed
                         : filter === 'all'       ? [...pending, ...completed]
                         : pending;

            if (!chosen.length) return `No ${filter === 'all' ? '' : filter + ' '}tasks found.`;

            const today = new Date().toISOString().slice(0, 10);
            const lines = chosen.map((t, i) => {
                const bits = [`${i + 1}. "${t.title}"`];
                if (t.priority) bits.push(`[${t.priority}]`);
                if (t.dueDate) {
                    // Stored dates are not always the YYYY-MM-DD the tool schema
                    // advertises — real data holds full ISO timestamps like
                    // "2026-07-31T00:00:00.000Z". Sending that to the model wastes
                    // tokens and invites it to read a meaningless midnight time
                    // back to the student. Trimming to the date is safe because
                    // ISO strings compare correctly on their first ten characters.
                    const due = String(t.dueDate).slice(0, 10);
                    bits.push(`due ${due}${t.dueTime ? ' ' + t.dueTime : ''}`);
                    if (!t.completed && due < today) bits.push('(OVERDUE)');
                } else {
                    bits.push('(no due date)');
                }
                if (t.completed) bits.push('(done)');
                return bits.join(' ');
            });

            return `${chosen.length} ${filter === 'all' ? '' : filter + ' '}task(s):\n${lines.join('\n')}`;
        }
    });

    register({
        name: 'create_task',
        description:
            'Create a new task or to-do item. Use when the student asks to add, ' +
            'create, or remember a task. Supports optional due date, due time, and priority.',
        parameters: {
            type: 'object',
            properties: {
                title:       { type: 'string', description: 'Short task title (required)' },
                priority:    { type: 'string', enum: ['high', 'medium', 'low'], description: 'Priority level — default medium' },
                description: { type: 'string', description: 'Optional longer description or notes' },
                dueDate:     { type: 'string', description: 'Due date in YYYY-MM-DD format' },
                dueTime:     { type: 'string', description: 'Due time in HH:MM 24-hour format (e.g. 14:30)' },
            },
            required: ['title']
        },
        handler({ title, priority = 'medium', description = '', dueDate = null, dueTime = null }) {
            if (!title) return 'Error: task title is required.';
            const task = {
                id:          Date.now().toString(),
                title,
                priority:    ['high', 'medium', 'low'].includes(priority) ? priority : 'medium',
                description,
                dueDate:     dueDate || null,
                dueTime:     dueTime || null,
                completed:   false,
                createdAt:   new Date().toISOString(),
            };
            window.Storage.addTask(task);
            if (typeof renderTasks === 'function') renderTasks();
            showNotification(`✅ Task created: "${title}"`, 'success');
            return `Task "${title}" created${dueDate ? ' (due ' + dueDate + ')' : ''}.`;
        }
    });

    register({
        name: 'edit_task',
        description:
            'Edit a SINGLE existing task. Use for one task only — if the student wants to ' +
            'update multiple tasks at once, use batch_edit_tasks instead (saves API calls). ' +
            'Do NOT use for marking complete (use complete_task) or deleting (use delete_task).',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the task title to identify it' },
                newTitle:     { type: 'string', description: 'New title (omit to keep current)' },
                priority:     { type: 'string', enum: ['high', 'medium', 'low'], description: 'New priority (omit to keep current)' },
                dueDate:      { type: 'string', description: 'New due date YYYY-MM-DD (omit to keep current)' },
                dueTime:      { type: 'string', description: 'New due time HH:MM (omit to keep current)' },
                description:  { type: 'string', description: 'New description (omit to keep current)' },
            },
            required: ['titleKeyword']
        },
        handler({ titleKeyword, newTitle, priority, dueDate, dueTime, description }) {
            const tasks  = window.Storage.loadTasks();
            const needle = titleKeyword.toLowerCase();
            const all    = [...(tasks.pending || []), ...(tasks.completed || [])];
            const match  = all.find(t => t.title.toLowerCase().includes(needle));
            if (!match) return `No task found matching "${titleKeyword}".`;

            const updated = { ...match };
            if (newTitle     !== undefined) updated.title       = newTitle;
            if (priority     !== undefined) updated.priority    = priority;
            if (dueDate      !== undefined) updated.dueDate     = dueDate;
            if (dueTime      !== undefined) updated.dueTime     = dueTime;
            if (description  !== undefined) updated.description = description;

            window.Storage.updateTask(match.id, updated);
            if (typeof renderTasks === 'function') renderTasks();
            showNotification(`✏️ Task updated: "${match.title}"`, 'success');
            return `Task "${match.title}" updated.`;
        }
    });

    register({
        name: 'complete_task',
        description:
            'Mark a pending task as done / completed. Use when the student says they ' +
            'finished, completed, or did a task. Do NOT use this to delete a task.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the task title to identify it' },
            },
            required: ['titleKeyword']
        },
        handler({ titleKeyword }) {
            const tasks  = window.Storage.loadTasks();
            const needle = titleKeyword.toLowerCase();
            const match  = (tasks.pending || []).find(t => t.title.toLowerCase().includes(needle));
            if (!match) return `No pending task found matching "${titleKeyword}".`;
            window.Storage.completeTask(match.id);
            if (typeof renderTasks === 'function') renderTasks();
            showNotification(`✅ Task completed: "${match.title}"`, 'success');
            return `Task "${match.title}" marked as completed.`;
        }
    });

    register({
        name: 'delete_task',
        description:
            'Permanently delete a task. Use only when the student explicitly asks to ' +
            'delete or remove a task (not just complete it). Always requires confirmation.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the task title to identify it' },
            },
            required: ['titleKeyword']
        },
        handler({ titleKeyword }) {
            const tasks  = window.Storage.loadTasks();
            const needle = titleKeyword.toLowerCase();
            const all    = [...(tasks.pending || []), ...(tasks.completed || [])];
            const match  = all.find(t => t.title.toLowerCase().includes(needle));
            if (!match) return `No task found matching "${titleKeyword}".`;
            if (!confirm(`Delete task "${match.title}"? This cannot be undone.`)) {
                return 'Deletion cancelled by user.';
            }
            window.Storage.deleteTask(match.id);
            if (typeof renderTasks === 'function') renderTasks();
            showNotification(`🗑️ Task deleted: "${match.title}"`, 'info');
            return `Task "${match.title}" deleted.`;
        }
    });

    register({
        name: 'batch_edit_tasks',
        description:
            'Edit multiple tasks at once in a single call. Use this instead of calling ' +
            'edit_task repeatedly when the student asks to update several tasks at once ' +
            '(e.g. "change all overdue tasks to Friday", "set all CALC3 tasks to high priority"). ' +
            'Each item in the edits array targets one task by keyword and applies the given fields.',
        parameters: {
            type: 'object',
            properties: {
                edits: {
                    type: 'array',
                    description: 'List of task edits to apply',
                    items: {
                        type: 'object',
                        properties: {
                            titleKeyword: { type: 'string', description: 'Keyword to identify the task' },
                            newTitle:     { type: 'string', description: 'New title (omit to keep current)' },
                            priority:     { type: 'string', enum: ['high', 'medium', 'low'], description: 'New priority level (omit to keep current)' },
                            dueDate:      { type: 'string', description: 'New due date YYYY-MM-DD (omit to keep current)' },
                            dueTime:      { type: 'string', description: 'New due time HH:MM 24-hour (omit to keep current)' },
                            description:  { type: 'string', description: 'New notes or description text (omit to keep current)' },
                        },
                        required: ['titleKeyword']
                    }
                }
            },
            required: ['edits']
        },
        handler({ edits }) {
            if (!Array.isArray(edits) || edits.length === 0) return 'No edits provided.';
            const tasks = window.Storage.loadTasks();
            const all   = [...(tasks.pending || []), ...(tasks.completed || [])];
            let updated = 0, notFound = [];

            for (const edit of edits) {
                const { titleKeyword, ...fields } = edit;
                const needle = (titleKeyword || '').toLowerCase();
                const match  = all.find(t => t.title.toLowerCase().includes(needle));
                if (!match) { notFound.push(titleKeyword); continue; }
                const updatedTask = { ...match };
                if (fields.newTitle    !== undefined) updatedTask.title       = fields.newTitle;
                if (fields.priority    !== undefined) updatedTask.priority    = fields.priority;
                if (fields.dueDate     !== undefined) updatedTask.dueDate     = fields.dueDate;
                if (fields.dueTime     !== undefined) updatedTask.dueTime     = fields.dueTime;
                if (fields.description !== undefined) updatedTask.description = fields.description;
                window.Storage.updateTask(match.id, updatedTask);
                updated++;
            }

            if (typeof renderTasks === 'function') renderTasks();
            const msg = `${updated} task${updated !== 1 ? 's' : ''} updated.` +
                (notFound.length ? ` Not found: ${notFound.join(', ')}.` : '');
            showNotification(`✏️ ${msg}`, updated > 0 ? 'success' : 'error');
            return msg;
        }
    });

    // ── NOTES HELPERS ──────────────────────────────────────────────────────────

    function _htmlToMarkdown(html) {
        if (!html || typeof html !== 'string') return '(Empty note)';

        // 1. Preserve <pre> and <code> blocks verbatim
        const codeBlocks = [];
        let preserved = html.replace(/<pre\b[^>]*>([\s\S]*?)<\/pre>|<code\b[^>]*>([\s\S]*?)<\/code>/gi, (match) => {
            const idx = codeBlocks.length;
            let cleanCode = match;
            cleanCode = cleanCode.replace(/^<pre\b[^>]*>/i, '').replace(/<\/pre>$/i, '');
            cleanCode = cleanCode.replace(/^<code\b[^>]*>/i, '').replace(/<\/code>$/i, '');
            cleanCode = cleanCode
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&apos;/g, "'")
                .replace(/&nbsp;/g, ' ');
            codeBlocks.push(`\n\`\`\`\n${cleanCode.trim()}\n\`\`\`\n`);
            return `__CODE_BLOCK_${idx}__`;
        });

        // 2. Headings
        preserved = preserved
            .replace(/<h1\b[^>]*>([\s\S]*?)<\/h1>/gi, '\n# $1\n')
            .replace(/<h2\b[^>]*>([\s\S]*?)<\/h2>/gi, '\n## $1\n')
            .replace(/<h3\b[^>]*>([\s\S]*?)<\/h3>/gi, '\n### $1\n')
            .replace(/<h[4-6]\b[^>]*>([\s\S]*?)<\/h[4-6]>/gi, '\n#### $1\n');

        // 3. Lists
        preserved = preserved
            .replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, '\n- $1')
            .replace(/<\/(ul|ol)>/gi, '\n');

        // 4. Block breaks & Paragraphs
        preserved = preserved
            .replace(/<\/(p|div|tr|blockquote)>/gi, '\n\n')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<hr\s*\/?>/gi, '\n---\n');

        // 5. Strip remaining HTML tags
        preserved = preserved.replace(/<[^>]+>/g, '');

        // 6. Decode HTML entities
        preserved = preserved
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&apos;/g, "'")
            .replace(/&nbsp;/g, ' ');

        // 7. Restore code blocks
        preserved = preserved.replace(/__CODE_BLOCK_(\d+)__/g, (_, idx) => codeBlocks[Number(idx)] || '');

        // 8. Collapse extra blank lines and trim
        const cleanText = preserved.replace(/\n\s*\n\s*\n+/g, '\n\n').trim();
        return cleanText || '(Empty note)';
    }

    function _findBestNoteMatch(notes, query) {
        if (!query || typeof query !== 'string') return { match: null, ambiguity: null };
        const needle = query.trim().toLowerCase();
        if (!needle) return { match: null, ambiguity: null };

        // Bucket 1: Exact ID Match
        const idMatches = notes.filter(n => n.id === query.trim() || String(n.id) === needle);
        if (idMatches.length === 1) return { match: idMatches[0], ambiguity: null };
        if (idMatches.length > 1) return { match: null, ambiguity: idMatches.map(n => n.title) };

        // Bucket 2: Exact Title Match (case insensitive)
        const exactTitleMatches = notes.filter(n => n.title && n.title.toLowerCase() === needle);
        if (exactTitleMatches.length === 1) return { match: exactTitleMatches[0], ambiguity: null };
        if (exactTitleMatches.length > 1) return { match: null, ambiguity: exactTitleMatches.map(n => n.title) };

        // Bucket 3: Title Starts With (case insensitive)
        const startsWithMatches = notes.filter(n => n.title && n.title.toLowerCase().startsWith(needle));
        if (startsWithMatches.length === 1) return { match: startsWithMatches[0], ambiguity: null };
        if (startsWithMatches.length > 1) return { match: null, ambiguity: startsWithMatches.map(n => n.title) };

        // Bucket 4: Title Contains (case insensitive)
        const containsMatches = notes.filter(n => n.title && n.title.toLowerCase().includes(needle));
        if (containsMatches.length === 1) return { match: containsMatches[0], ambiguity: null };
        if (containsMatches.length > 1) return { match: null, ambiguity: containsMatches.map(n => n.title) };

        // Bucket 5: Category Contains (case insensitive)
        const categoryMatches = notes.filter(n => n.category && n.category.toLowerCase().includes(needle));
        if (categoryMatches.length === 1) return { match: categoryMatches[0], ambiguity: null };
        if (categoryMatches.length > 1) return { match: null, ambiguity: categoryMatches.map(n => n.title) };

        return { match: null, ambiguity: null };
    }

    // ── NOTES ──────────────────────────────────────────────────────────────────

    register({
        name: 'list_notes',
        description:
            'List or read the student\'s saved notes. ' +
            'Call without arguments to list available note titles and categories. ' +
            'Provide "titleKeyword" whenever asked about the contents, details, summary, or text of a specific note. ' +
            'Searches note titles, categories, and IDs.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: {
                    type: 'string',
                    description: 'Optional keyword from note title, category, or exact note ID to read full body content'
                }
            }
        },
        handler({ titleKeyword } = {}) {
            const notes = window.Storage.loadNotes() || [];
            if (!notes.length) {
                return JSON.stringify({ status: 'empty', message: 'No notes found.' });
            }

            if (titleKeyword !== undefined && titleKeyword !== null && String(titleKeyword).trim() !== '') {
                const { match, ambiguity } = _findBestNoteMatch(notes, String(titleKeyword));

                if (ambiguity && ambiguity.length > 0) {
                    return JSON.stringify({
                        status: 'ambiguous',
                        message: `Multiple notes matched "${titleKeyword}". Please specify exact title.`,
                        matches: ambiguity
                    });
                }

                if (!match) {
                    const titles = notes.map(n => n.title);
                    return JSON.stringify({
                        status: 'not_found',
                        message: `No note found matching "${titleKeyword}".`,
                        availableTitles: titles
                    });
                }

                const MAX_NOTE_CHARS = 12000;
                let textContent = _htmlToMarkdown(match.content);
                let truncated = false;
                const totalCharacters = textContent.length;

                if (totalCharacters > MAX_NOTE_CHARS) {
                    textContent = textContent.slice(0, MAX_NOTE_CHARS) +
                        `\n\n[TRUNCATED: Note content exceeded ${MAX_NOTE_CHARS} characters. Total length: ${totalCharacters} characters]`;
                    truncated = true;
                }

                return JSON.stringify({
                    status: 'success',
                    id: match.id,
                    title: match.title,
                    category: match.category || 'General',
                    updatedAt: match.updatedAt || match.createdAt || null,
                    content: textContent,
                    truncated,
                    totalCharacters
                });
            }

            const lines = notes.map((n, i) =>
                `${i + 1}. "${n.title}"${n.category ? ' [' + n.category + ']' : ''} (id: ${n.id})`
            );
            return `Found ${notes.length} note(s):\n${lines.join('\n')}`;
        }
    });


    register({
        name: 'create_note',
        description:
            'Create a new note with a title, HTML content, and a category. ' +
            'CONTENT FORMAT — must be valid HTML: use <h2>, <h3>, <p>, <ul>, <ol>, <li>, <strong>, <em>, <code>, <blockquote> tags. ' +
            'Do NOT use markdown — no *asterisks*, no **double asterisks**, no # headings, no backtick fences. ' +
            'MERMAID DIAGRAMS are supported — wrap diagram code in <pre><code class="language-mermaid">...</code></pre> and they will render visually in Reading Mode. Use diagrams generously for flows, sequences, and architectures. ' +
            'SINGLE NOTE RULE — always put all content for a topic in ONE note, no matter how long. Never split a topic across multiple notes. ' +
            'Always set a category so the note is easy to find.',
        parameters: {
            type: 'object',
            properties: {
                title:    { type: 'string', description: 'Note title (required)' },
                content:  { type: 'string', description: 'Full note body as valid HTML. Use <h2>/<h3> for sections, <ul>/<li> for lists, <strong> for bold, <em> for italic, <code> for inline code. For diagrams: <pre><code class="language-mermaid">graph LR\n  A-->B</code></pre>. No markdown.' },
                category: { type: 'string', description: 'Category label e.g. "Artificial Intelligence", "Statics", "Mathematics" (required — always set this)' },
            },
            required: ['title', 'category']
        },
        handler({ title, content = '', category = null }) {
            if (!title) return 'Error: note title is required.';
            const notes = window.Storage.loadNotes();
            const note  = {
                id:        Date.now().toString(),
                title,
                content,
                category:  category || null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            notes.push(note);
            window.Storage.saveNotes(notes);
            if (typeof loadNotes === 'function') loadNotes();
            showNotification(`📝 Note created: "${title}"`, 'success');
            return `Note "${title}" created${category ? ' in category "' + category + '"' : ''}.`;
        }
    });

    register({
        name: 'edit_note',
        description:
            'Edit an existing note — update its title, content, or category. ' +
            'Use when the student asks to update, fix, rename, recategorise, or rewrite a note. ' +
            'Call list_notes first to get exact titles if unsure. ' +
            'Content must be valid HTML — use <h2>, <p>, <ul>, <li>, <strong>, <em> tags. No markdown. ' +
            'Mermaid diagrams: <pre><code class="language-mermaid">...</code></pre> — they render visually in Reading Mode.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the current note title to identify it' },
                newTitle:     { type: 'string', description: 'New title (omit to keep current)' },
                newContent:   { type: 'string', description: 'Complete new HTML content to replace the note body (omit to keep current)' },
                newCategory:  { type: 'string', description: 'New category label e.g. "Statics", "Mathematics" (omit to keep current)' },
            },
            required: ['titleKeyword']
        },
        handler({ titleKeyword, newTitle, newContent, newCategory }) {
            const notes  = window.Storage.loadNotes();
            const needle = titleKeyword.toLowerCase();
            const idx    = notes.findIndex(n => n.title.toLowerCase().includes(needle));
            if (idx === -1) return `No note found matching "${titleKeyword}". Call list_notes to see all notes.`;

            const note = { ...notes[idx] };
            if (newTitle    !== undefined) note.title    = newTitle;
            if (newContent  !== undefined) note.content  = newContent;
            if (newCategory !== undefined) note.category = newCategory;
            note.updatedAt = new Date().toISOString();

            notes[idx] = note;
            window.Storage.saveNotes(notes);
            if (typeof loadNotes === 'function') loadNotes();
            showNotification(`📝 Note updated: "${note.title}"`, 'success');
            const changes = [
                newTitle    !== undefined ? 'title'    : null,
                newContent  !== undefined ? 'content'  : null,
                newCategory !== undefined ? 'category' : null,
            ].filter(Boolean).join(', ');
            return `Note "${note.title}" updated (${changes}).`;
        }
    });

    register({
        name: 'append_to_note',
        description:
            'Add HTML content to the end of an existing note without replacing it. ' +
            'Use when the student asks to add to, continue, or append to a note. ' +
            'Do NOT use this to replace content — for that use edit_note instead. ' +
            'Content must be valid HTML tags.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the note title to identify it' },
                content:      { type: 'string', description: 'HTML content to append (use <p>, <ul>, <h2> etc. tags)' },
            },
            required: ['titleKeyword', 'content']
        },
        handler({ titleKeyword, content }) {
            const notes  = window.Storage.loadNotes();
            const needle = titleKeyword.toLowerCase();
            const idx    = notes.findIndex(n => n.title.toLowerCase().includes(needle));
            if (idx === -1) return `No note found matching "${titleKeyword}". Call list_notes to see all notes.`;

            const note = notes[idx];
            notes[idx] = {
                ...note,
                content:   note.content + content,
                updatedAt: new Date().toISOString(),
            };
            window.Storage.saveNotes(notes);
            showNotification(`📝 Content added to "${note.title}"`, 'success');
            return `Content appended to note "${note.title}".`;
        }
    });

    register({
        name: 'delete_note',
        description:
            'Permanently delete a single note. Use only when the student explicitly asks to ' +
            'delete or remove a note. Call list_notes first to confirm the exact title — ' +
            'never guess or claim success without actually finding a match. ' +
            'For deleting multiple notes use this tool once per note.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the note title to identify it' },
            },
            required: ['titleKeyword']
        },
        handler({ titleKeyword }) {
            const notes  = window.Storage.loadNotes();
            const needle = titleKeyword.toLowerCase();
            const match  = notes.find(n => n.title.toLowerCase().includes(needle));
            if (!match) return `No note found matching "${titleKeyword}". Call list_notes to see all available notes.`;
            if (!confirm(`Delete note "${match.title}"? This cannot be undone.`)) {
                return 'Deletion cancelled by user.';
            }
            const updated = notes.filter(n => n.id !== match.id);
            window.Storage.saveNotes(updated);
            if (typeof loadNotes === 'function') loadNotes();
            showNotification(`🗑️ Note deleted: "${match.title}"`, 'info');
            return `✅ Note "${match.title}" has been deleted. ${updated.length} note(s) remain.`;
        }
    });

    register({
        name: 'delete_all_notes',
        description:
            'Delete ALL notes permanently. Use only when the student explicitly asks to ' +
            'delete all notes, clear all notes, or start fresh. Requires confirmation.',
        parameters: {
            type: 'object',
            properties: {}
        },
        handler() {
            const notes = window.Storage.loadNotes();
            if (!notes.length) return 'No notes to delete.';
            const count = notes.length;
            if (!confirm(`Delete ALL ${count} notes permanently? This cannot be undone.`)) {
                return 'Deletion cancelled by user.';
            }
            window.Storage.saveNotes([]);
            if (typeof loadNotes === 'function') loadNotes();
            showNotification(`🗑️ All ${count} notes deleted.`, 'info');
            return `✅ All ${count} notes have been deleted.`;
        }
    });

    // ── EVENTS ─────────────────────────────────────────────────────────────────

    register({
        name: 'create_event',
        description:
            'Create a calendar event or scheduled item. Use when the student asks to ' +
            'schedule, add to calendar, or create an event. Requires a title and date.',
        parameters: {
            type: 'object',
            properties: {
                title:       { type: 'string', description: 'Event title (required)' },
                date:        { type: 'string', description: 'Date in YYYY-MM-DD format (required)' },
                startTime:   { type: 'string', description: 'Start time HH:MM 24-hour (default 09:00)' },
                endTime:     { type: 'string', description: 'End time HH:MM 24-hour (optional)' },
                description: { type: 'string', description: 'Optional event description' },
            },
            required: ['title', 'date']
        },
        handler({ title, date, startTime = '09:00', endTime = null, description = '' }) {
            if (!title || !date) return 'Error: title and date are required.';
            const event = {
                title, date, startTime,
                endTime:     endTime || null,
                description,
                color:       '#7c6ff7',
                repeat:      'none',
            };
            window.Storage.addEvent(event);
            if (window.Schedule) {
                window.Schedule.renderWeek(window.Schedule._currentWeekOffset || 0);
            }
            showNotification(`📅 Event created: "${title}"`, 'success');
            return `Event "${title}" on ${date} at ${startTime} created.`;
        }
    });

    register({
        name: 'delete_event',
        description:
            'Permanently delete a calendar event. Use only when the student explicitly ' +
            'asks to delete or remove an event. Always requires confirmation.',
        parameters: {
            type: 'object',
            properties: {
                titleKeyword: { type: 'string', description: 'A keyword from the event title to identify it' },
            },
            required: ['titleKeyword']
        },
        handler({ titleKeyword }) {
            const events = window.Storage.loadEvents();
            const needle = titleKeyword.toLowerCase();
            const match  = events.find(e => e.title.toLowerCase().includes(needle));
            if (!match) return `No event found matching "${titleKeyword}".`;
            if (!confirm(`Delete event "${match.title}"? This cannot be undone.`)) {
                return 'Deletion cancelled by user.';
            }
            window.Storage.deleteEvent(match.id);
            if (window.Schedule) {
                window.Schedule.renderWeek(window.Schedule._currentWeekOffset || 0);
            }
            showNotification(`🗑️ Event deleted: "${match.title}"`, 'info');
            return `Event "${match.title}" deleted.`;
        }
    });

    // ── TIMER ──────────────────────────────────────────────────────────────────

    register({
        name: 'control_timer',
        description:
            'Control the study timer. Use when the student asks to start, pause, reset, ' +
            'or switch the timer to Pomodoro mode. ' +
            'Actions: "start" — begin timing | "pause" — pause timer | ' +
            '"reset" — reset to zero | "set_pomodoro" — switch to 25-min Pomodoro mode.',
        parameters: {
            type: 'object',
            properties: {
                action: {
                    type: 'string',
                    enum: ['start', 'pause', 'reset', 'set_pomodoro'],
                    description: 'Timer action to perform'
                },
            },
            required: ['action']
        },
        handler({ action }) {
            switch (action) {
                case 'start':
                    window.Timer?.startTimer?.();
                    showNotification('⏱️ Timer started!', 'success');
                    return 'Timer started.';
                case 'pause':
                    window.Timer?.pauseTimer?.();
                    showNotification('⏸️ Timer paused.', 'info');
                    return 'Timer paused.';
                case 'reset':
                    window.Timer?.resetTimer?.();
                    showNotification('🔄 Timer reset.', 'info');
                    return 'Timer reset.';
                case 'set_pomodoro':
                    window.Timer?.setPomodoroMode?.();
                    showNotification('🍅 Switched to Pomodoro mode.', 'success');
                    return 'Timer set to Pomodoro mode.';
                default:
                    return `Unknown timer action: "${action}". Use start, pause, reset, or set_pomodoro.`;
            }
        }
    });

    // ── QUIZZES ────────────────────────────────────────────────────────────────

    register({
        name: 'quiz_action',
        description:
            'Quiz and flashcard actions. Three modes: ' +
            '"load" — open an existing quiz by title keyword | ' +
            '"flashcards" — start flashcard study mode for an existing quiz | ' +
            '"generate" — create a new AI quiz on a topic or from content. ' +
            'Use titleKeyword for load/flashcards, topic for generate.',
        parameters: {
            type: 'object',
            properties: {
                action:       { type: 'string', enum: ['load', 'flashcards', 'generate'], description: 'Quiz action to perform' },
                titleKeyword: { type: 'string', description: 'Keyword from quiz title (for load and flashcards)' },
                topic:        { type: 'string', description: 'Topic or content to generate a quiz about (for generate)' },
                questionCount: { type: 'integer', description: 'How many questions, when the student asks for a number (for generate). Omit if they did not say.' },
            },
            required: ['action']
        },
        handler({ action, titleKeyword, topic, questionCount }) {
            switch (action) {
                case 'load': {
                    if (!titleKeyword) return 'Error: titleKeyword required for load.';
                    const quizzes = window.allQuizzes || [];
                    const needle  = titleKeyword.toLowerCase();
                    const match   = quizzes.find(q => q.title.toLowerCase().includes(needle));
                    if (!match) return `No quiz found matching "${titleKeyword}".`;
                    if (typeof showSection === 'function') showSection('quizzes');
                    setTimeout(() => window.Quiz?.loadQuiz?.(match.id), 300);
                    showNotification(`📝 Loading quiz: "${match.title}"`, 'success');
                    return `Quiz "${match.title}" loaded.`;
                }
                case 'flashcards': {
                    if (!titleKeyword) return 'Error: titleKeyword required for flashcards.';
                    const quizzes = window.allQuizzes || [];
                    const needle  = titleKeyword.toLowerCase();
                    const match   = quizzes.find(q => q.title.toLowerCase().includes(needle));
                    if (!match) return `No quiz found matching "${titleKeyword}".`;
                    if (typeof showSection === 'function') showSection('quizzes');
                    setTimeout(() => window.Quiz?.startFlashcardMode?.(match.id), 300);
                    showNotification(`🃏 Starting flashcards: "${match.title}"`, 'success');
                    return `Flashcard mode started for "${match.title}".`;
                }
                case 'generate': {
                    if (typeof showSection === 'function') showSection('ai-quiz');
                    // generateFromTopic renders the form and waits for it. Earlier versions
                    // guessed with a fixed 400 ms and then a 6 s poll (QA AI-5). Any failure
                    // is shown to the student: an empty form with no message is the one
                    // outcome that cannot be acted on.
                    Promise.resolve(window.Quiz?.generateFromTopic?.({ topic, questionCount }))
                        .catch(err => {
                            console.error('[quiz_action] generate failed:', err);
                            showNotification("Couldn't start the quiz. Please try again.", 'error');
                        });
                    return `AI quiz generation started${topic ? ' for: ' + topic : ''}${questionCount ? ` (${questionCount} questions)` : ''}.`;
                }
                default:
                    return `Unknown quiz action: "${action}". Use load, flashcards, or generate.`;
            }
        }
    });

    // ── FOCUS MODE ─────────────────────────────────────────────────────────────

    register({
        name: 'start_focus',
        description:
            'Activate Focus Mode for a deep-work session. Optionally pre-selects a task ' +
            'to focus on. Use when the student asks to start focusing, enter focus mode, ' +
            'or wants to work on a specific task.',
        parameters: {
            type: 'object',
            properties: {
                taskKeyword: { type: 'string', description: 'Optional keyword from a task title to focus on' },
            },
        },
        handler({ taskKeyword } = {}) {
            if (typeof showSection === 'function') showSection('focus');
            if (taskKeyword) {
                const tasks  = window.Storage.loadTasks();
                const needle = taskKeyword.toLowerCase();
                const match  = (tasks.pending || []).find(t => t.title.toLowerCase().includes(needle));
                if (match) window._lazyCall?.('focus', 'focusSelectTask', match.id);
            }
            showNotification('🎯 Focus mode activated!', 'success');
            return `Focus mode activated${taskKeyword ? ' on task matching "' + taskKeyword + '"' : ''}.`;
        }
    });

    // ── NAVIGATION ─────────────────────────────────────────────────────────────

    register({
        name: 'navigate',
        description:
            'Navigate to a section of the app. Use when the student asks to go to, ' +
            'open, or show a specific section. ' +
            'Do NOT use this to make a quiz, flashcards or a note: opening the AI Quiz screen ' +
            'leaves an empty form. To make a quiz call quiz_action with action "generate". ' +
            'Valid sections: dashboard, notes, tasks, schedule, timer, quizzes, ' +
            'ai-quiz, focus, music, pdf-reader, library, settings.',
        parameters: {
            type: 'object',
            properties: {
                section: {
                    type: 'string',
                    enum: ['dashboard', 'notes', 'tasks', 'schedule', 'timer', 'quizzes',
                           'ai-quiz', 'focus', 'music', 'pdf-reader', 'library', 'settings'],
                    description: 'The section to navigate to'
                },
            },
            required: ['section']
        },
        handler({ section }) {
            if (typeof showSection === 'function') showSection(section);
            return `Navigated to ${section}.`;
        }
    });

    // ── APPEARANCE ─────────────────────────────────────────────────────────────

    register({
        name: 'set_theme',
        description:
            'Change the app colour theme. Use when the student asks to change the theme, ' +
            'appearance, or colour scheme of the app.',
        parameters: {
            type: 'object',
            properties: {
                theme: {
                    type: 'string',
                    enum: ['dark-premium', 'light-clean', 'vibrant', 'soft-warm'],
                    description: 'The theme to apply'
                },
            },
            required: ['theme']
        },
        handler({ theme }) {
            if (typeof applyTheme === 'function') applyTheme(theme);
            showNotification(`🎨 Theme changed to ${theme}.`, 'success');
            return `Theme set to ${theme}.`;
        }
    });

    // ── Public API ─────────────────────────────────────────────────────────────

    console.log(`%c🧰 ToolRegistry loaded — ${Object.keys(_tools).length} tools registered`, 'color: #10b981; font-weight: bold');

    return { register, getSchemas, execute, has, list };

})();

export { ToolRegistry };
export default ToolRegistry;

// Transition shim. meneer-chat.js reads window.ToolRegistry; remove only once
// nothing references the global.
if (typeof window !== 'undefined') window.ToolRegistry = ToolRegistry;
